// Object.groupBy polyfill for browsers that don't support it natively (Chrome <117, Firefox <119, Safari <17.4)
export function groupBy(
  items: Iterable<unknown>,
  callbackFn: (element: unknown, index: number) => PropertyKey,
): Record<PropertyKey, unknown[]> {
  const result: Record<PropertyKey, unknown[]> = Object.create(null)
  let index = 0
  for (const item of items) {
    const key = callbackFn(item, index++)
    const existing = result[key]
    if (existing) {
      existing.push(item)
    } else {
      result[key] = [item]
    }
  }
  return result
}

if (typeof Object.groupBy !== 'function') {
  Object.groupBy = groupBy as typeof Object.groupBy
}

// Array.prototype.at polyfill for browsers that don't support it natively (Chrome <92, Firefox <90, Safari <15.4).
// Without it the landing layout's `matches.at(-1)` threw during hydration and blanked the home page — issue #83.
export function at(this: unknown, index: number): unknown {
  const items = Object(this) as ArrayLike<unknown>
  const length = Math.trunc(items.length) || 0
  const relativeIndex = Math.trunc(index) || 0
  const resolved = relativeIndex < 0 ? length + relativeIndex : relativeIndex
  return resolved >= 0 && resolved < length ? items[resolved] : undefined
}

// Defined rather than assigned so it stays non-enumerable, like the native method: an enumerable addition to
// Array.prototype leaks into every `for…in` over an array.
if (typeof Array.prototype.at !== 'function') {
  // oxlint-disable-next-line no-extend-native -- standing in for a missing builtin IS extending the native
  Object.defineProperty(Array.prototype, 'at', { value: at, writable: true, configurable: true })
}

// String.prototype.replaceAll polyfill for browsers that don't support it natively (Chrome <85, Firefox <77,
// Safari <13.1). Same class of break as `at` above: `/docs` renders the OpenAPI description through it.
const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g

export function replaceAll(
  this: string,
  pattern: string | RegExp,
  replacement: string | ((substring: string, ...args: unknown[]) => string),
): string {
  if (pattern instanceof RegExp && !pattern.global) {
    throw new TypeError('replaceAll must be called with a global RegExp')
  }
  const everyMatch =
    pattern instanceof RegExp ? pattern : new RegExp(pattern.replace(REGEXP_SPECIAL_CHARS, '\\$&'), 'g')
  // Delegate to `replace` so the engine's own `$&` / `$1` and function-replacer semantics apply unchanged; the two
  // branches differ only in which `replace` overload TypeScript picks.
  return typeof replacement === 'string' ? this.replace(everyMatch, replacement) : this.replace(everyMatch, replacement)
}

if (typeof String.prototype.replaceAll !== 'function') {
  // oxlint-disable-next-line no-extend-native -- standing in for a missing builtin IS extending the native
  Object.defineProperty(String.prototype, 'replaceAll', { value: replaceAll, writable: true, configurable: true })
}
